import { useState } from 'react';

import { ModalBase } from './ModalBase';

import type React from 'react';

export interface SubstituirAtendenteModalProps {
  open: boolean;
  /** Nome do atendente que será substituído (null degrada honesto). */
  attendantName: string | null;
  /** Mantido por compat de chamada — a prosa do modal não expõe mais o id cru. */
  conversationId?: string;
  isPending: boolean;
  failed: boolean;
  onClose: () => void;
  /** Executa o force-takeover; o caller navega à Tela 3 ao sucesso (D1/D9). */
  onConfirm: (reason: string) => void;
}

/** Piso de UI mais estrito que o backend (force-takeover exige ≥20). */
const REASON_FLOOR = 30;

/**
 * SubstituirAtendenteModal — entrada da Tela 3 (Intervenção) a partir da Tela 2 (D9).
 * Diferente do ForceTakeoverModal (que assume de H_c/IA e permanece no espelho), aqui
 * a supervisora assume a conversa para **operá-la publicamente**: o backend é o mesmo
 * `force-takeover`, mas ao confirmar o caller navega à Tela 3. Guard anti duplo-submit.
 */
export function SubstituirAtendenteModal({
  open,
  attendantName,
  isPending,
  failed,
  onClose,
  onConfirm,
}: SubstituirAtendenteModalProps): React.JSX.Element | null {
  const [reason, setReason] = useState('');
  if (!open) return null;
  const trimmedLen = reason.trim().length;
  const canSubmit = trimmedLen >= REASON_FLOOR && !isPending;

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      title="Substituir atendente"
      width={560}
      footer={
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <button className="btn-ghost-mini" type="button" onClick={onClose} disabled={isPending}>
            Cancelar
          </button>
          <button
            className="btn-ghost-mini btn-ghost-mini--primary"
            type="button"
            disabled={!canSubmit}
            onClick={() => {
              onConfirm(reason);
            }}
          >
            {isPending ? 'Assumindo…' : 'Assumir e operar'}
          </button>
        </div>
      }
    >
      <div className="modal-form">
        <div className="modal-form__callout">
          Você assumirá esta conversa e passará a<b> operá-la publicamente</b> como supervisora.
          {attendantName !== null && (
            <>
              {' '}
              <b>{attendantName}</b> ficará em modo somente leitura.
            </>
          )}
        </div>
        <label className="modal-form__field">
          <span className="modal-form__label">
            Motivo da intervenção (obrigatório · mínimo {REASON_FLOOR} caracteres)
          </span>
          <textarea
            className="modal-form__textarea"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
            rows={4}
            placeholder="Assumindo para conduzir diretamente — o caso exige intervenção da supervisão…"
            aria-label="Motivo da intervenção"
          />
          <span className="modal-form__hint">
            {trimmedLen}/{REASON_FLOOR} mínimo
          </span>
        </label>
        <div className="modal-form__callout modal-form__callout--warn">
          ⚠ O audit registra a substituição com o motivo obrigatório.
        </div>
        {failed && (
          <div className="banner banner--danger" role="alert">
            <div className="banner__body">
              <span className="banner__title">Não foi possível assumir a conversa.</span>
            </div>
          </div>
        )}
      </div>
    </ModalBase>
  );
}
